import React from 'react'
import styled, { keyframes } from 'styled-components'
import { Button, Chip, Tooltip, IconButton, useMediaQuery, CircularProgress } from '@mui/material'
import AssessmentIcon from '@mui/icons-material/Assessment'
import RefreshIcon from '@mui/icons-material/Refresh'
import EmailIcon from '@mui/icons-material/Email'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import VerifiedIcon from '@mui/icons-material/Verified'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DownloadIcon from '@mui/icons-material/Download'
import ShareIcon from '@mui/icons-material/Share'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DescriptionIcon from '@mui/icons-material/Description'
import TableChartIcon from '@mui/icons-material/TableChart'

const ReportHeader = ({
  report,
  buildingName,
  reportType,
  reportTypeName,
  onGenerateNew,
  canGenerateNew,
  onSendEmail,
  onExportPdf,
  onExportWord,
  onExportCsv,
  isExportingPdf,
  isExportingWord,
  isExportingCsv
}) => {
  const isMobile = useMediaQuery('(max-width: 600px)')
  const isTablet = useMediaQuery('(max-width: 900px)')

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getQuestionsAnswered = () => {
    if (!report?.answers) return { answered: 0, total: 0, percentage: 0 }
    const total = report.answers.length
    const answered = report.answers.filter(a => {
      // Use the noInfoFound flag, or check for empty/placeholder answers
      if (a.noInfoFound) return false
      if (!a.answer) return false
      // Structured answers (CPSE.7 sub-enquiry maps) are objects — treat any non-empty map as answered.
      if (typeof a.answer !== 'string') {
        return Object.values(a.answer).some(v => typeof v === 'string' && v.trim() !== '')
      }
      const lowerAnswer = a.answer.toLowerCase()
      if (lowerAnswer.includes('information not found')) return false
      if (lowerAnswer.includes('no information found')) return false
      return true
    }).length
    const percentage = total > 0 ? Math.round((answered / total) * 100) : 0
    return { answered, total, percentage }
  }

  const { answered, total, percentage } = getQuestionsAnswered()

  // Get color based on percentage for the icon
  const getPercentageColor = (pct) => {
    if (pct >= 90) return { bg: '#D1FAE5', color: '#059669' }
    if (pct >= 75) return { bg: '#ECFDF5', color: '#10B981' }
    if (pct >= 60) return { bg: '#F7FEE7', color: '#84CC16' }
    if (pct >= 45) return { bg: '#FEF9C3', color: '#EAB308' }
    if (pct >= 30) return { bg: '#FFEDD5', color: '#F97316' }
    return { bg: '#FEE2E2', color: '#DC2626' }
  }
  const percentageColors = getPercentageColor(percentage)

  return (
    <HeaderContainer>
      <HeaderMain>
        <TitleSection>
          <IconWrapper>
            <AssessmentIcon />
          </IconWrapper>
          <TitleContent>
            <PageTitle>{report?.reportType?.name || reportTypeName || 'Report'}</PageTitle>
            <BuildingName>{buildingName}</BuildingName>
          </TitleContent>
        </TitleSection>

        <ActionsSection>
          {onExportPdf && (
            <Tooltip title="Download PDF report" arrow>
              {isMobile ? (
                <ActionIconButton onClick={onExportPdf} disabled={isExportingPdf}>
                  {isExportingPdf ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                </ActionIconButton>
              ) : (
                <ActionButton
                  variant="outlined"
                  startIcon={isExportingPdf ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
                  onClick={onExportPdf}
                  disabled={isExportingPdf}
                >
                  {!isTablet && (isExportingPdf ? 'Exporting...' : 'PDF')}
                </ActionButton>
              )}
            </Tooltip>
          )}
          {onExportWord && (
            <Tooltip title="Download editable Word document" arrow>
              {isMobile ? (
                <ActionIconButton onClick={onExportWord} disabled={isExportingWord}>
                  {isExportingWord ? <CircularProgress size={20} /> : <DescriptionIcon />}
                </ActionIconButton>
              ) : (
                <ActionButton
                  variant="outlined"
                  startIcon={isExportingWord ? <CircularProgress size={18} /> : <DescriptionIcon />}
                  onClick={onExportWord}
                  disabled={isExportingWord}
                >
                  {!isTablet && (isExportingWord ? 'Exporting...' : 'Word')}
                </ActionButton>
              )}
            </Tooltip>
          )}
          {onExportCsv && (
            <Tooltip title="Download CSV spreadsheet" arrow>
              {isMobile ? (
                <ActionIconButton onClick={onExportCsv} disabled={isExportingCsv}>
                  {isExportingCsv ? <CircularProgress size={20} /> : <TableChartIcon />}
                </ActionIconButton>
              ) : (
                <ActionButton
                  variant="outlined"
                  startIcon={isExportingCsv ? <CircularProgress size={18} /> : <TableChartIcon />}
                  onClick={onExportCsv}
                  disabled={isExportingCsv}
                >
                  {!isTablet && (isExportingCsv ? 'Exporting...' : 'CSV')}
                </ActionButton>
              )}
            </Tooltip>
          )}
          {onSendEmail && (
            <Tooltip title="Email a per-recipient link — every view is logged to their email" arrow>
              {isMobile ? (
                <ActionIconButton onClick={onSendEmail}>
                  <EmailIcon />
                </ActionIconButton>
              ) : (
                <ActionButton
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={onSendEmail}
                >
                  {!isTablet && 'Email Report'}
                </ActionButton>
              )}
            </Tooltip>
          )}
          <Tooltip title={canGenerateNew ? "Generate a new version" : "Report generation in progress"} arrow>
            <span>
              {isMobile ? (
                <PrimaryIconButton
                  onClick={onGenerateNew}
                  disabled={!canGenerateNew}
                >
                  <RefreshIcon />
                </PrimaryIconButton>
              ) : (
                <PrimaryButton
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={onGenerateNew}
                  disabled={!canGenerateNew}
                >
                  {isTablet ? 'New Report' : 'Generate New Report'}
                </PrimaryButton>
              )}
            </span>
          </Tooltip>
        </ActionsSection>
      </HeaderMain>

      <MetadataSection>
        <MetadataGrid>
          <MetadataCard>
            <MetadataIcon>
              <CalendarTodayIcon />
            </MetadataIcon>
            <MetadataContent>
              <MetadataLabel>Generated</MetadataLabel>
              <MetadataValue>
                {isMobile
                  ? formatShortDate(report?.completedAt)
                  : formatDate(report?.completedAt)}
              </MetadataValue>
            </MetadataContent>
          </MetadataCard>

          <MetadataCard>
            <MetadataIcon variant="primary">
              <VerifiedIcon />
            </MetadataIcon>
            <MetadataContent>
              <MetadataLabel>Version</MetadataLabel>
              <MetadataValue>{report?.version || '1.0.0'}</MetadataValue>
            </MetadataContent>
          </MetadataCard>

          <MetadataCard>
            <MetadataIcon style={{ background: percentageColors.bg, color: percentageColors.color }}>
              <CheckCircleIcon />
            </MetadataIcon>
            <MetadataContent>
              <MetadataLabel>Questions Answered</MetadataLabel>
              <MetadataValue>
                <AnswerProgress>
                  <AnswerCount style={{ color: percentageColors.color }}>{answered}/{total}</AnswerCount>
                  <ProgressBar>
                    <ProgressFill percentage={percentage} />
                  </ProgressBar>
                </AnswerProgress>
              </MetadataValue>
            </MetadataContent>
          </MetadataCard>
        </MetadataGrid>
      </MetadataSection>

      <HeaderDivider />
    </HeaderContainer>
  )
}

export default ReportHeader

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const HeaderContainer = styled.div`
  margin-bottom: 28px;
  animation: ${fadeIn} 0.4s ease-out;
`

const HeaderMain = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
  }
`

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;

  @media (max-width: 600px) {
    gap: 14px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, #B83D1F 100%);
  border-radius: 16px;
  color: white;
  box-shadow: 0 8px 20px rgba(210, 71, 36, 0.25);

  svg {
    font-size: 32px;
  }

  @media (max-width: 600px) {
    width: 52px;
    height: 52px;
    border-radius: 14px;

    svg {
      font-size: 26px;
    }
  }
`

const TitleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`

const BuildingName = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin: 0;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`

const ActionsSection = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 100%;
    justify-content: flex-end;
  }
`

const ActionButton = styled(Button)`
  && {
    border-radius: 10px;
    padding: 10px 18px;
    font-weight: 600;
    text-transform: none;
    border-color: ${({ theme }) => theme.colors.lightGrey};
    color: ${({ theme }) => theme.colors.haiti};
    transition: all 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.primary}08;
    }

    @media (max-width: 900px) {
      padding: 10px 14px;
      min-width: auto;
    }
  }
`

const ActionIconButton = styled(IconButton)`
  && {
    width: 44px;
    height: 44px;
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 10px;
    color: ${({ theme }) => theme.colors.haiti};
    transition: all 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.primary}08;
    }
  }
`

const PrimaryButton = styled(Button)`
  && {
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 600;
    text-transform: none;
    background: ${({ theme }) => theme.colors.secondary};
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(51, 190, 204, 0.3);
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: #2BA8B9;
      box-shadow: 0 6px 16px rgba(51, 190, 204, 0.4);
      transform: translateY(-1px);
    }

    &:disabled {
      background: ${({ theme }) => theme.colors.grey[200]};
      box-shadow: none;
    }

    @media (max-width: 900px) {
      padding: 10px 16px;
    }
  }
`

const PrimaryIconButton = styled(IconButton)`
  && {
    width: 44px;
    height: 44px;
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: 10px;
    color: white;
    box-shadow: 0 4px 12px rgba(51, 190, 204, 0.3);
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: #2BA8B9;
      box-shadow: 0 6px 16px rgba(51, 190, 204, 0.4);
    }

    &:disabled {
      background: ${({ theme }) => theme.colors.grey[200]};
      box-shadow: none;
    }
  }
`

const MetadataSection = styled.div`
  margin-bottom: 24px;
`

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const MetadataCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: linear-gradient(to right, #ffffff, #fafafa);
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    border-color: ${({ theme }) => theme.colors.primary}40;
  }

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`

const MetadataIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: ${({ variant, theme }) => {
    switch (variant) {
      case 'primary':
        return `${theme.colors.primary}12`
      case 'success':
        return '#D1FAE5'
      default:
        return '#F3F4F6'
    }
  }};
  border-radius: 10px;
  color: ${({ variant, theme }) => {
    switch (variant) {
      case 'primary':
        return theme.colors.primary
      case 'success':
        return '#10B981'
      default:
        return theme.colors.grey[200]
    }
  }};
  flex-shrink: 0;

  svg {
    font-size: 22px;
  }

  @media (max-width: 600px) {
    width: 38px;
    height: 38px;

    svg {
      font-size: 20px;
    }
  }
`

const MetadataContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const MetadataLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey[200]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MetadataValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};

  @media (max-width: 600px) {
    font-size: 14px;
  }
`

const AnswerProgress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`

const AnswerCount = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #E5E7EB;
  border-radius: 3px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ percentage }) => percentage}%;
  background: ${({ percentage }) => {
    if (percentage >= 90) return '#059669'; // Deep green
    if (percentage >= 75) return '#10B981'; // Green
    if (percentage >= 60) return '#84CC16'; // Lime
    if (percentage >= 45) return '#EAB308'; // Yellow
    if (percentage >= 30) return '#F97316'; // Orange
    return '#DC2626'; // Red
  }};
  border-radius: 3px;
  transition: width 0.5s ease, background 0.3s ease;
`

const HeaderDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.primary}30,
    ${({ theme }) => theme.colors.lightGrey},
    transparent
  );
`
